import { NavLink, useLocation } from 'react-router-dom';
import {
  Activity,
  Building2,
  ChevronsLeft,
  ChevronsRight,
  Cloud,
  Globe,
  Hash,
  Headphones,
  History,
  LayoutDashboard,
  Layers,
  ListChecks,
  Phone,
  PhoneCall,
  Plug,
  Server,
  LogOut,
  ShieldCheck,
  UserRound,
  Users,
  type LucideIcon,
} from 'lucide-react';
import { NAV_SECTIONS, SETTINGS_NAV_ITEM, navLabelForRole, type NavItem } from '@/config/nav';
import { usePermissions } from '@/shared/hooks/usePermissions';
import { useAuth, useIsPlatformAdmin } from '@/providers/AuthProvider';
import { useTenant } from '@/providers/TenantProvider';
import { useShell } from '@/providers/ShellProvider';
import { SetupProgressCompact } from './SetupProgressCompact';
import { BrandMark } from '@/shared/components/BrandMark';
import { cn } from '@/lib/utils';
import { Separator } from '@/shared/components/ui/separator';
import { Button } from '@/shared/components/ui/button';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/shared/components/ui/tooltip';
import { Sheet, SheetContent } from '@/shared/components/ui/sheet';

const ICONS: Record<string, LucideIcon> = {
  LayoutDashboard,
  ListChecks,
  Layers,
  Cloud,
  Phone,
  Hash,
  Headphones,
  Users,
  ShieldCheck,
  Activity,
  PhoneCall,
  History,
  Globe,
  Building2,
  Plug,
  Server,
  Settings: UserRound,
  UserRound,
};

/** True only for the nav item that owns the current path (not every sibling). */
function isNavItemActive(pathname: string, href: string) {
  if (href === '/dashboard') {
    return pathname === '/dashboard' || pathname === '/';
  }
  // Phone Numbers is nested under /telephony — keep Telephony connections hub separate
  if (href === '/telephony') {
    return (
      pathname === '/telephony' ||
      (pathname.startsWith('/telephony/') &&
        !pathname.startsWith('/telephony/phone-numbers'))
    );
  }
  if (href === '/telephony/phone-numbers') {
    return pathname.startsWith('/telephony/phone-numbers');
  }
  if (href === '/settings/organization') {
    return pathname.startsWith('/settings/organization');
  }
  if (href === '/settings/account') {
    return pathname === href || pathname.startsWith(`${href}/`);
  }
  return pathname === href || pathname.startsWith(`${href}/`);
}

function NavLinkItem({
  item,
  label,
  collapsed,
  onNavigate,
}: {
  item: NavItem;
  label: string;
  collapsed: boolean;
  onNavigate?: () => void;
}) {
  const { pathname } = useLocation();
  const Icon = ICONS[item.icon] ?? LayoutDashboard;
  const isActive = isNavItemActive(pathname, item.href);

  const link = (
    <NavLink
      to={item.href}
      end={
        item.href === '/dashboard' ||
        item.href === '/configure' ||
        item.href === '/settings/account' ||
        item.href === '/settings/organization' ||
        item.href === '/telephony/phone-numbers'
      }
      onClick={onNavigate}
      title={collapsed ? label : undefined}
      aria-current={isActive ? 'page' : undefined}
      className={cn(
        'relative flex items-center gap-3 rounded-md py-2 text-sm font-medium transition-all duration-200',
        collapsed ? 'justify-center px-2' : 'px-3',
        isActive
          ? 'border-l-2 border-primary bg-primary/10 text-primary shadow-sm'
          : 'border-l-2 border-transparent text-muted-foreground hover:bg-muted hover:text-foreground'
      )}
    >
      <Icon
        className={cn(
          'h-4 w-4 shrink-0 transition-colors duration-200',
          isActive ? 'text-primary' : 'text-muted-foreground'
        )}
      />
      <span
        className={cn(
          'truncate transition-all duration-200',
          collapsed ? 'sr-only w-0 opacity-0' : 'opacity-100'
        )}
      >
        {label}
      </span>
    </NavLink>
  );

  if (!collapsed) return link;

  // Do not asChild the NavLink — Radix Tooltip breaks RR className/active styling
  // and made every collapsed item look selected.
  return (
    <Tooltip delayDuration={0}>
      <TooltipTrigger asChild>
        <span className="block w-full">{link}</span>
      </TooltipTrigger>
      <TooltipContent side="right" className="font-medium">
        {label}
      </TooltipContent>
    </Tooltip>
  );
}

function SideNavBody({
  collapsed,
  onNavigate,
}: {
  collapsed: boolean;
  onNavigate?: () => void;
}) {
  const { can, canAny, role } = usePermissions();
  const { logout } = useAuth();
  const isPlatformAdmin = useIsPlatformAdmin();
  const { hasTenant } = useTenant();
  const location = useLocation();
  const { toggleCollapsed } = useShell();

  /**
   * Keep Voicera Admin on the platform sidebar when they have no tenant selected
   * (including My Account / settings) so the nav does not "jump" to tenant menus.
   */
  const usePlatformNav =
    location.pathname.startsWith('/platform') || (isPlatformAdmin && !hasTenant);

  const filterItem = (item: NavItem) => {
    if (item.platformOnly && !isPlatformAdmin) return false;
    if (item.tenantOnly && usePlatformNav) return false;
    if (item.roles && !item.roles.includes(role)) return false;
    if (item.requiredPermissions) {
      const ok =
        item.requiredPermissions.length === 1
          ? can(item.requiredPermissions[0])
          : canAny(item.requiredPermissions);
      if (!ok) return false;
    }
    return true;
  };

  const sections = NAV_SECTIONS.map((section) => ({
    ...section,
    items: section.items.filter(filterItem),
  })).filter((s) => s.items.length > 0);

  const showSettings = filterItem(SETTINGS_NAV_ITEM);

  return (
    <div className="flex h-full flex-col">
      <div
        className={cn(
          'flex h-14 items-center gap-1 border-b border-border',
          collapsed ? 'justify-center px-2' : 'justify-between px-3'
        )}
      >
        <NavLink
          to="/dashboard"
          className={cn(
            'min-w-0 transition-opacity duration-200',
            collapsed ? '' : 'flex-1'
          )}
          aria-label="VEE Connector home"
        >
          <BrandMark compact={collapsed} showName={!collapsed} />
        </NavLink>
        <Button
          type="button"
          variant="ghost"
          size="icon"
          className="hidden h-8 w-8 shrink-0 text-muted-foreground hover:text-primary md:inline-flex"
          aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          onClick={toggleCollapsed}
        >
          {collapsed ? (
            <ChevronsRight className="h-4 w-4" />
          ) : (
            <ChevronsLeft className="h-4 w-4" />
          )}
        </Button>
      </div>

      <nav className="flex-1 space-y-1 overflow-y-auto overflow-x-hidden p-2">
        {sections.map((section) => (
          <div key={section.id} className="mb-3">
            {!collapsed && (
              <p className="mb-1.5 px-3 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground/80">
                {section.label}
              </p>
            )}
            {collapsed && <Separator className="mx-auto mb-2 w-6" />}
            <div className="space-y-0.5">
              {section.items.map((item) => (
                <NavLinkItem
                  key={item.id}
                  item={item}
                  label={navLabelForRole(item, role)}
                  collapsed={collapsed}
                  onNavigate={onNavigate}
                />
              ))}
            </div>
          </div>
        ))}

        {showSettings && (
          <>
            <Separator className="my-2" />
            <NavLinkItem
              item={SETTINGS_NAV_ITEM}
              label={navLabelForRole(SETTINGS_NAV_ITEM, role)}
              collapsed={collapsed}
              onNavigate={onNavigate}
            />
          </>
        )}

        <Separator className="my-2" />
        {collapsed ? (
          <Tooltip delayDuration={0}>
            <TooltipTrigger asChild>
              <span className="block w-full">
                <button
                  type="button"
                  onClick={() => void logout()}
                  className="flex w-full items-center justify-center rounded-md px-2 py-2 text-sm text-muted-foreground hover:bg-muted hover:text-foreground"
                  aria-label="Sign out"
                >
                  <LogOut className="h-4 w-4" />
                </button>
              </span>
            </TooltipTrigger>
            <TooltipContent side="right" className="font-medium">
              Sign out
            </TooltipContent>
          </Tooltip>
        ) : (
          <button
            type="button"
            onClick={() => void logout()}
            className="flex w-full items-center gap-3 rounded-md px-3 py-2 text-sm font-medium text-muted-foreground hover:bg-muted hover:text-foreground"
          >
            <LogOut className="h-4 w-4 shrink-0" />
            <span>Sign out</span>
          </button>
        )}
      </nav>

      {!usePlatformNav && hasTenant && role !== 'USER' && !collapsed && (
        <div className="border-t border-border p-3 animate-in fade-in duration-200">
          <SetupProgressCompact />
        </div>
      )}
    </div>
  );
}

/** Permission-filtered sidebar — collapsible on desktop, drawer on mobile. */
export function SideNav() {
  const { collapsed, mobileOpen, setMobileOpen } = useShell();

  return (
    <>
      {/* Desktop sidebar */}
      <aside
        className={cn(
          'relative z-30 hidden h-full shrink-0 flex-col border-r border-border bg-card transition-[width] duration-300 ease-in-out md:flex',
          collapsed ? 'w-[4.25rem]' : 'w-64'
        )}
      >
        <SideNavBody collapsed={collapsed} />
      </aside>

      {/* Mobile drawer */}
      <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
        <SheetContent side="left" className="w-72 border-r border-border bg-card p-0">
          <SideNavBody collapsed={false} onNavigate={() => setMobileOpen(false)} />
        </SheetContent>
      </Sheet>
    </>
  );
}
